import {
  AlertTriangle,
  Video,
  Clock,
  Calendar,
  Wifi,
  TrendingDown,
  Target,
  Zap,
  MessageSquare,
  LayoutList,
  Users,
  Repeat,
  LucideIcon,
} from 'lucide-react'

export interface ConsequenceItem {
  icon: LucideIcon
  text: string
  sub: string
}

export interface CredibilityPair {
  left: string
  right: string
}

export interface RoleItem {
  icon: LucideIcon
  label: string
  humor: boolean
}

export const symptomsItems = [
  'Långa helgruppsdiskussioner utan tydlig output',
  'Otydlig agenda och svag mötesstruktur',
  'Några pratar, andra är tysta',
  'Ingen gemensam yta för fortsatt arbete',
  'Krångliga hybridmöten – några på plats, några på distans',
  'Ändlösa diskussioner om 2–3 dagar på kontoret',
]

export const needsItems = [
  'Asynkrona arbetssätt som faktiskt fungerar',
  'Struktur för kanaler och informationsflöden',
  'Grundläggande faciliteringskompetens',
  'Tydlig koppling mellan kontors- och hemarbetsdagar',
  'Möten med syfte – inte möten som vana',
]

export const consequences: ConsequenceItem[] = [
  {
    icon: TrendingDown,
    text: 'Medarbetare tappar energi och engagemang',
    sub: 'Dåliga möten är tröttande, inte bara ineffektiva.',
  },
  {
    icon: AlertTriangle,
    text: 'Chefer upplever digitalt ledarskap som svårt',
    sub: 'Ingen har lärt dem hur det faktiskt fungerar.',
  },
  {
    icon: Clock,
    text: 'Beslut drar ut på tiden',
    sub: 'Utan struktur skjuts allt till nästa möte.',
  },
  {
    icon: Calendar,
    text: 'Onödigt många möten bokas',
    sub: 'Mötet ersätter tänkandet istället för att möjliggöra det.',
  },
  {
    icon: Wifi,
    text: 'Den digitala mognaden utvecklas inte',
    sub: 'Verktyg används – men inte rätt.',
  },
  {
    icon: Video,
    text: 'Hybridmodellen ifrågasätts',
    sub: 'Trots att problemet är arbetssättet, inte modellen.',
  },
]

export const credibilityPairs: CredibilityPair[] = [
  { left: 'Möte som kopierar kontoret', right: 'Möte designat för distans' },
  {
    left: 'Alla pratar samtidigt',
    right: 'Individuell reflektion före diskussion',
  },
  { left: 'Helgrupp hela tiden', right: 'Smågrupper före helgrupp' },
  { left: 'Mötet är arbetsytan', right: 'Digital yta som lever mellan möten' },
  { left: 'Synkront per reflex', right: 'Asynkront som standard' },
]

export const roles: RoleItem[] = [
  {
    icon: LayoutList,
    label: 'Mötesdesign med tydlig output och struktur',
    humor: false,
  },
  {
    icon: MessageSquare,
    label: 'Individuell reflektion före gemensam diskussion',
    humor: false,
  },
  {
    icon: Users,
    label: 'Smågrupper före helgrupp – fler perspektiv kommer fram',
    humor: false,
  },
  {
    icon: Zap,
    label: 'Digital arbetsyta som lever mellan möten',
    humor: false,
  },
  {
    icon: Repeat,
    label: 'Klara principer för synkront vs asynkront arbete',
    humor: false,
  },
]

export const rolesHumor =
  'Och någon som faktiskt stänger mötet när det är klart – inte 10 minuter efter.'

export const workPrinciples = [
  'Vi börjar med att kartlägga hur arbetet faktiskt ser ut – inte hur det borde se ut',
  'Vi tränar nyckelpersoner i facilitering och digital kommunikation',
  'Vi bygger hållbara hybridrutiner som inte kräver konstant underhåll',
  'Vi kopplar arbetssättet till prestation och lönsamhet – inte bara trivsel',
  'Vi arbetar iterativt – testar, justerar, förankrar',
]

export const results = [
  'Möten blir kortare och mer fokuserade',
  'Fler perspektiv kommer fram – inte bara de högljuddas',
  'Beslut blir tydligare och tas snabbare',
  'Hemarbete upplevs som produktivt – inte isolerat',
  'Hybridarbete höjer prestation istället för att sänka den',
]

export const resultsHumor =
  'Och du slipper den där känslan av att ha suttit i möten hela dagen utan att ha gjort något.'

export const ctaPersonas = [
  {
    icon: Target,
    title: 'VD',
    text: 'Du märker att hybridarbete kostar mer än det ger.',
  },
  {
    icon: Users,
    title: 'Chef',
    text: 'Du leder ett team som aldrig är på samma plats samtidigt.',
  },
  {
    icon: Calendar,
    title: 'HR',
    text: 'Du äger policyn — men inte arbetssättet.',
  },
  {
    icon: Video,
    title: 'Teamledare',
    text: 'Möten tar all tid utan att ge resultat.',
  },
]
